import { useState, useCallback } from 'react';
import axios from 'axios';
import { enhancedApi } from '@/shared/services/enhancedApi';
import type { TravelDetailsData, PlanSelection } from '../types/customer-portal';
import { usePlanSelection } from '../contexts/PlanSelectionContext';
import { useAppStore } from '@/store/useAppStore';
import { useLocation } from "react-router-dom";
import { getUtmQsParams } from '@/shared/utils/utmUtils';

// Plan option interface from usePlanRates
interface PlanOption {
  productId: number;
  productName: string;
  productLabel: string;
  maxBenefitLimit: number;
  planType: string;
  rate: {
    rateAmount: number;
    currency: {
      currencyId: number;
      currencyCode: string;
      symbol: string;
    };
  };
  benefitsCategories: Array<{
    benefitsCategory: string;
    categoryCoverage: string;
    benefits: Array<{
      benefitName: string;
      benefitCoverage: string;
    }>;
  }>;
  exclusion: string[];
  // Additional fields that might come from the API response
  policy_prefix?: string;
  travel_type_id?: number;
  type_name?: string;
}

interface VerifyEmailRequest {
  email: string;
  planPage: string;
  isMultiTrip: boolean;
  planType?: number;
  startTrip?: string;
  destinations?: string; // Changed from string[] to string to match API expectation
  endTrip?: string;
  purpose?: string;
  startDate: string;
  endDate?: string;
  productId: number;
  productName: string;
  productLabel: string;
  rateAmount: number;
  currencyId: number;
  currencyCode: string;
  currencySymbol: string; // Changed from 'symbol' to 'currencySymbol'
  policyPrefix?: string;
  travelerTypeId: number;
  typeName: string;
  mbl: string; // Max Benefit Limit from selectedPlan.maxBenefitLimit
  convertedRateAmount?: number,
  convertedCurrencyCode?: string,
  convertedCurrencyId?: number,
}

interface VerifyEmailResponse {
  success: boolean;
  code: number;
  message: string;
  isVerified?: boolean;
  data?: unknown;
  tempDataId?: string;
  principal?: {
    principalId?: number;
    principal_id?: number;
    email?: string;
    [key: string]: unknown;
  };
}

interface UseVerifyEmailAddressReturn {
  verifyEmail: (
    email: string,
    travelData: TravelDetailsData,
    planSelection: PlanSelection,
    selectedPlan: PlanOption
  ) => Promise<VerifyEmailResponse>;
  loading: boolean;
  error: string | null;
  success: boolean;
  clearState: () => void;
}

export const useVerifyEmailAddress = (): UseVerifyEmailAddressReturn => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);

  const { setPaymentPrice, setPaymentConvertedPrice, setAdjustmentPriceByAge } = useAppStore();
  
  // Access tempData from PlanSelectionContext to check for forex conversion
  const { getTempDataValue } = usePlanSelection();

  // Helper function to get currency values (converted or original)
  const getCurrencyValues = useCallback((selectedPlan: PlanOption) => {
    // Check if forex conversion has occurred by examining tempData
    const isForexConverted = getTempDataValue('isForexConverted') as boolean;
    const convertedRateAmount = getTempDataValue('convertedRateAmount') as number;
    const convertedCurrencyCode = getTempDataValue('convertedCurrencyCode') as string;
    const convertedCurrencyId = getTempDataValue('convertedCurrencyId') as number;
    
    console.log('useVerifyEmailAddress: Checking for forex conversion:', {
      isForexConverted,
      convertedRateAmount,
      convertedCurrencyCode,
      convertedCurrencyId,
      originalRateAmount: selectedPlan.rate.rateAmount,
      originalCurrencyCode: selectedPlan.rate.currency.currencyCode,
      originalCurrencyId: selectedPlan.rate.currency.currencyId
    });
    
    // If forex conversion has occurred and we have valid converted values, use them
    if (isForexConverted &&
        convertedRateAmount &&
        convertedCurrencyCode &&
        convertedCurrencyId) {
      console.log('useVerifyEmailAddress: Using converted currency values for email verification');
      return {
        rateAmount: convertedRateAmount,
        currencyCode: convertedCurrencyCode,
        currencyId: convertedCurrencyId,
        currencySymbol: convertedCurrencyCode === 'PHP' ? '₱' :
                       convertedCurrencyCode === 'USD' ? '$' :
                       selectedPlan.rate.currency.symbol // fallback to original symbol
      };
    }
    
    // Otherwise, use original plan currency values
    console.log('useVerifyEmailAddress: Using original currency values for email verification');
    return {
      rateAmount: selectedPlan.rate.rateAmount,
      currencyCode: selectedPlan.rate.currency.currencyCode,
      currencyId: selectedPlan.rate.currency.currencyId,
      currencySymbol: selectedPlan.rate.currency.symbol
    };
  }, [getTempDataValue]);

  // Convert plan type string to number
  const convertPlanTypeToNumber = useCallback((planType: string): number => {
    switch (planType.toLowerCase()) {
      case 'individual':
        return 1;
      case 'family':
        return 2;
      case 'group':
        return 3;
      case 'multi-trip':
        return 0;        
      default:
        console.warn(`Unknown plan type: ${planType}, defaulting to Individual (1)`);
        return 1;
    }
  }, []);

  // Convert plan type from landing page format to API format
  const convertLandingPlanTypeToApiNumber = useCallback((landingPlanType: PlanSelection['planType']): number => {
    switch (landingPlanType) {
      case 'Individual Plan':
        return 1;
      case 'Family Plan':
        return 2;
      case 'Group Plan':
        return 3;
      case 'Multi Trip':
        return 0; // Multi Trip has its own distinct plan type ID
      default:
        console.warn(`Unknown landing plan type: ${landingPlanType}, defaulting to Individual (1)`);
        return 1;
    }
  }, []);

  // Convert plan type to URL slug for planPage
  const convertPlanTypeToSlug = useCallback((planType: string): string => {
    const typeMap: Record<string, string> = {
      'Individual Plan': 'individual',
      'Individual': 'individual',
      'Family Plan': 'family',
      'Family': 'family',
      'Group Plan': 'group',
      'Group': 'group',
      'Multi Trip': 'multi-trip'
    };
    return typeMap[planType] || 'individual';
  }, []);

  // Generate plan page URL
  const generatePlanPageUrl = useCallback((planType: string): string => {
    const planSlug = convertPlanTypeToSlug(planType);
    return `customer-portal/client-information/${planSlug}`;
  }, [convertPlanTypeToSlug]);

  // Format destinations array to string
  const formatDestinations = useCallback((destinations: string[]): string => {
    if (!Array.isArray(destinations) || destinations.length === 0) {
      return '';
    }
    return destinations.join(', ');
  }, []);

  // Get proper traveler type ID and name based on plan type
  const getTravelerTypeInfo = useCallback((planType: string, selectedPlan: PlanOption) => {
    // Use selectedPlan data if available, otherwise derive from planType
    if (selectedPlan.travel_type_id && selectedPlan.type_name) {
      return {
        travelerTypeId: selectedPlan.travel_type_id,
        typeName: selectedPlan.type_name
      };
    }

    // Fallback based on plan type
    switch (planType.toLowerCase()) {
      case 'individual':
      case 'individual plan':
        return { travelerTypeId: 1, typeName: 'Individual' };
      case 'family':
      case 'family plan':
        return { travelerTypeId: 2, typeName: 'Family' };
      case 'group':
      case 'group plan':
        return { travelerTypeId: 3, typeName: 'Group' };
      case 'multi trip':
        return { travelerTypeId: 4, typeName: 'Multi Trip' }; // Multi-trip has its own distinct traveler type ID
      default:
        return { travelerTypeId: 1, typeName: 'Individual' };
    }
  }, []);

  // Main verify email function
  const verifyEmail = useCallback(async (
    email: string,
    travelData: TravelDetailsData,
    planSelection: PlanSelection,
    selectedPlan: PlanOption
  ): Promise<VerifyEmailResponse> => {
    setLoading(true);
    setError(null);
    setSuccess(false);
    try {
      // Validate required inputs
      if (!email || !email.trim()) {
        throw new Error('Email address is required');
      }

      if (!travelData) {
        throw new Error('Travel data is required');
      }

      if (!planSelection) {
        throw new Error('Plan selection is required');
      }

      if (!selectedPlan) {
        throw new Error('Selected plan is required');
      }

      // Determine plan type number
      let planTypeNumber: number;
      if (travelData.tripType === 'Annual Multi-Trip') {
        // For Multi-Trip, use the original plan selection from landing page
        planTypeNumber = convertLandingPlanTypeToApiNumber(planSelection.planType);
      } else {
        // For Single Trip, use the plan type from travel data
        planTypeNumber = convertPlanTypeToNumber(travelData.planType);
      }
console.log('planType being passed to getTravelerTypeInfo:', travelData.tripType === 'Annual Multi-Trip' ? planSelection.planType : travelData.planType) 
      // Get proper traveler type information
      const travelerTypeInfo = getTravelerTypeInfo(
        travelData.planType,
        selectedPlan
      );
console.log('travelerTypeInfo result:', planSelection, travelData.planType, travelerTypeInfo) 

      // Determine if this is a multi-trip request
      const isMultiTrip = travelData.tripType === 'Annual Multi-Trip';

      // Helper function to get maxBenefitLimit as string with validation
      const getMaxBenefitLimitString = (): string => {
        if (selectedPlan.maxBenefitLimit && typeof selectedPlan.maxBenefitLimit === 'number' && selectedPlan.maxBenefitLimit > 0) {
          return selectedPlan.maxBenefitLimit.toString();
        }
        // Default fallback value if maxBenefitLimit is not available or invalid
        console.warn('useVerifyEmailAddress: maxBenefitLimit not available or invalid, using default value "0"');
        return '0';
      };

      const isForexConverted = getTempDataValue('isForexConverted') as boolean
      // Get currency values (converted or original) based on forex conversion status
      const currencyValues = getCurrencyValues(selectedPlan);

      // Prepare request body with corrected field formats
      const requestBody: VerifyEmailRequest = {
        email: email.trim(),
        planPage: 'plan-details', // Use static value as required by API
        isMultiTrip: isMultiTrip,
        // For multi-trip, use travelDate; for single trip, use startDate
        startDate: isMultiTrip ? (travelData.travelDate || '') : (travelData.startDate || ''),
        productId: selectedPlan.productId,
        productName: selectedPlan.productName,
        productLabel: selectedPlan.productLabel,
        rateAmount: currencyValues.rateAmount, // Use converted amount if available
        currencyId: currencyValues.currencyId, // Use converted currency ID if available
        currencyCode: currencyValues.currencyCode, // Use converted currency code if available
        currencySymbol: currencyValues.currencySymbol, // Use appropriate symbol for converted currency
        travelerTypeId: travelerTypeInfo.travelerTypeId,
        typeName: travelerTypeInfo.typeName,
        purpose: travelData.purposeOfTravel || "",
        mbl: getMaxBenefitLimitString() // Max Benefit Limit from selectedPlan.maxBenefitLimit
      };

      // Add single-trip specific fields only if not multi-trip
      if (!isMultiTrip) {
        requestBody.planType = planTypeNumber;
        requestBody.startTrip = travelData.startTrip || '';
        requestBody.destinations = formatDestinations(travelData.destination || []); // Convert array to string
        requestBody.endTrip = travelData.endTrip || '';
        requestBody.endDate = travelData.endDate || '';
        requestBody.policyPrefix = selectedPlan.policy_prefix || 'PC'; // Default to 'PC' if not provided
      }

      if(isForexConverted){
        requestBody.convertedCurrencyCode = selectedPlan.rate.currency.currencyCode;
        requestBody.convertedRateAmount = selectedPlan.rate.rateAmount;
        requestBody.convertedCurrencyId = requestBody.currencyId;
      }

      console.log('Sending email verification request with forex-aware currency values:', {
        ...requestBody,
        mbl: requestBody.mbl, // Highlight the mbl field
        currencyInfo: {
          rateAmount: requestBody.rateAmount,
          currencyCode: requestBody.currencyCode,
          currencyId: requestBody.currencyId,
          currencySymbol: requestBody.currencySymbol,
          isForexConverted: getTempDataValue('isForexConverted') || false
        }
      });

      const utmQsParams = getUtmQsParams(queryParams);

      // Make API call
      const data: VerifyEmailResponse = await enhancedApi.post('/api/verification' + utmQsParams, requestBody, {
        timeout: 15000, // 15 second timeout for email operations
        headers: {
          'Content-Type': 'application/json',
        }
      }, 1); // High priority for email verification

      if (data.success) {
        // TODO: update other variable with principal data
        if(data.principal){
          if(isForexConverted){
            setPaymentConvertedPrice({
              currencyCode: requestBody.convertedCurrencyCode!,
              rateAmount: requestBody.convertedRateAmount!,
              currencyId: requestBody.convertedCurrencyId!
            });
          }
          
          setPaymentPrice({
              currencyCode: requestBody.currencyCode,
              rateAmount: requestBody.rateAmount,
              currencyId: requestBody.currencyId
          });
          setAdjustmentPriceByAge(0);
        }

        console.log('Email verification sent successfully:', data, data.principal);
        setSuccess(true);
        return data;
      } else {
        throw new Error(data.message || 'Failed to send email verification');
      }

    } catch (err) {
      console.error('Error sending email verification:', err);
      
      let errorMessage = 'Failed to send email verification';
      
      if (axios.isAxiosError(err)) {
        if (err.response) {
          // Server responded with error status
          const serverMessage = err.response.data?.message || err.response.statusText;
          errorMessage = `Server error (${err.response.status}): ${serverMessage}`;
        } else if (err.request) {
          // Request was made but no response received
          errorMessage = 'Network error: Unable to connect to server. Please check your internet connection.';
        } else {
          // Something else happened
          errorMessage = `Request error: ${err.message}`;
        }
      } else if (err instanceof Error) {
        errorMessage = err.message;
      }
      
      setError(errorMessage);
      
      // Return error response
      return {
        success: false,
        code: axios.isAxiosError(err) && err.response?.status ? err.response.status : 500,
        message: errorMessage
      };

    } finally {
      setLoading(false);
    }
  }, [convertPlanTypeToNumber, convertLandingPlanTypeToApiNumber, generatePlanPageUrl, formatDestinations, getTravelerTypeInfo, getCurrencyValues, getTempDataValue]);

  // Clear all state
  const clearState = useCallback(() => {
    setLoading(false);
    setError(null);
    setSuccess(false);
  }, []);

  return {
    verifyEmail,
    loading,
    error,
    success,
    clearState
  };
};
